import { type User } from '@sentry/react'
import { type UserGroup } from 'common/utils/types'
import React, { type ReactNode, useMemo } from 'react'

import { ReactComponent as SocialIcon } from '../assets/certificates/social.svg'
import { ReactComponent as DigitalIcon } from '../assets/certificates/digital.svg'
import { ReactComponent as SecurityIcon } from '../assets/certificates/security.svg'
import { ReactComponent as TrainerIcon } from '../assets/certificates/trainer.svg'
import { ReactComponent as AiIcon } from '../assets/certificates/ai.svg'
import { ReactComponent as CheckboxIcon } from '../assets/checkboxChecked.svg'

import { type Certificate, type CertificateDate, CertificateSlug } from './certificates.types'

export const getCertificatesCheckboxIcon = (slug: string | undefined): ReactNode => {
  switch (slug) {
    case CertificateSlug.trainer:
      return <CheckboxIcon fill={'#31528E'} width={20} height={20} />
    case CertificateSlug.digital:
      return <CheckboxIcon fill={'#70C1AC'} width={20} height={20} />
    case CertificateSlug.social:
      return <CheckboxIcon fill={'#EA9A56'} width={20} height={20} />
    case CertificateSlug.security:
      return <CheckboxIcon fill={'#5C9ACA'} width={20} height={20} />
    case CertificateSlug.ai:
      return <CheckboxIcon fill={'#E76F58'} width={20} height={20} />
  }
}

export const getCertificateIcon = (slug: string, height?: number, width?: number): JSX.Element => {
  switch (slug) {
    case CertificateSlug.security:
      return <SecurityIcon height={height} width={width} />
    case CertificateSlug.trainer:
      return <TrainerIcon height={height} width={width} />
    case CertificateSlug.digital:
      return <DigitalIcon height={height} width={width} />
    case CertificateSlug.social:
      return <SocialIcon height={height} width={width} />
    case CertificateSlug.ai:
      return <AiIcon height={height} width={width} />
    default:
      return <></>
  }
}

export interface CertificateDatesInfo {
  certificationDate?: Date
  recertificationDate?: Date
  certificationDuration?: number
  recertificationDuration?: number
}

export const getCertificateDatesInfo = (certificate: Certificate): CertificateDatesInfo => {
  if (!certificate || certificate.dates.length === 0) {
    return {}
  }

  const certificateDatesInfo: CertificateDatesInfo = {}
  const sortedDates: CertificateDate[] = [...certificate.dates].sort((a, b) => {
    return new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime()
  })

  sortedDates.forEach((date) => {
    const dateToCheck = new Date(date.validFrom)

    if (!certificateDatesInfo.certificationDate || dateToCheck < certificateDatesInfo.certificationDate) {
      certificateDatesInfo.certificationDate = dateToCheck
      certificateDatesInfo.certificationDuration = date.validFor
    } else if (
      certificateDatesInfo.certificationDate &&
      (!certificateDatesInfo.recertificationDate || dateToCheck > certificateDatesInfo.recertificationDate)
    ) {
      certificateDatesInfo.recertificationDate = dateToCheck
      certificateDatesInfo.recertificationDuration = date.validFor
    }
  })

  return certificateDatesInfo
}

export const getValidityCertificateDate = (consultant: User | UserGroup): Date | null => {
  const currentDate = new Date()
  const validDatesInPast = []
  const validDatesInFuture = []

  if (consultant?.certificates) {
    for (const certificate of consultant.certificates) {
      for (const date of certificate.dates) {
        const validTo = new Date(date.validTo)
        if (validTo > currentDate) {
          validDatesInFuture.push(validTo)
        } else {
          validDatesInPast.push(validTo)
        }
      }
    }

    if (validDatesInFuture.length > 0) {
      const nearestDateInFuture = new Date(Math.min(...validDatesInFuture.map((date) => date.getTime())))
      return nearestDateInFuture
    } else if (validDatesInPast.length > 0) {
      const nearestDateInPast = new Date(Math.max(...validDatesInPast.map((date) => date.getTime())))
      return nearestDateInPast
    }
  }

  return null
}

export const useIsDatePassed = (earliestValidTo: Date | null): boolean => {
  return useMemo(() => {
    return earliestValidTo ? earliestValidTo < new Date() : false
  }, [earliestValidTo])
}
